import { Box } from "@material-ui/core";
import React, {
  Suspense,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import clsx from "clsx";
import { isCorpTenant } from "@capone/common";
import { useDeviceTypes, LoadingPopup, B2BSpinner } from "halifax";
import { Route, RouteComponentProps } from "react-router-dom";
import { CallState, REVIEW_DETAILS_VR_CHECKOUT } from "redmond";
import { ClientContext } from "../../App";
import {
  AVAILABLE,
  CREDIT_OFFER_STACKING_V1,
  getExperimentVariant,
  TRAVEL_CREDIT_HISTORY_EXPERIMENT,
  TRAVEL_WALLET_CREDITS_EXPERIMENT,
  TRAVEL_WALLET_OFFER_EXPERIMENT,
  TREES_MODAL_EXPERIMENT,
  useExperiments,
} from "../../context/experiments";
import {
  PATH_VACATION_RENTALS_BOOK,
  PATH_VACATION_RENTALS_BOOK_CONFIRMATION,
} from "../../utils/paths";
import { VacationRentalBookBookConnectorProps } from "./container";
import { PORTAL_TITLE, VACATION_RENTAL_CHECKOUT_TITLE } from "./textConstants";
import { trackEvent } from "../../api/v0/analytics/trackEvent";
import { VRBookingErrorModal } from "./components/VRBookingErrorModal";
import { BookingInProgressModal } from "./components/BookingInProgressModal";
import { BookingSuccessModal } from "./components/BookingSuccessModal";
import { config } from "../../api/config";
import { useVacationRentalBookListing } from "./utils/useVacationRentalBookListing";

const CorpDesktopVacationRentalsBookWorkflow = React.lazy(
  () =>
    import("./components/capone-corporate/DesktopVacationRentalsBookWorkflow")
);
const CorpMobileVacationRentalsBookWorkflow = React.lazy(
  () =>
    import("./components/capone-corporate/MobileVacationRentalsBookWorkflow")
);
const ConsumerDesktopVacationRentalsBookWorkflow = React.lazy(
  () => import("./components/capone/DesktopVacationRentalsBookWorkflow")
);
const ConsumerMobileVacationRentalsBookWorkflow = React.lazy(
  () => import("./components/capone/MobileVacationRentalsBookWorkflow")
);

export interface IVacationRentalBookProps
  extends VacationRentalBookBookConnectorProps,
  RouteComponentProps { }

export const VacationRentalBook = (props: IVacationRentalBookProps) => {
  const {
    history,
    location,
    selectedHome,
    travelers,
    userPassengerCallState,
    viewedVacationRentalDetailsProperties,
  } = props;

  const { matchesMobile, matchesDesktop } = useDeviceTypes();
  const [primaryTravelerId, setPrimaryTravelerId] = useState<
    string | undefined
  >(undefined);
  const [searchedPrimaryTraveler, setSearchedPrimaryTraveler] = useState(false);

  const userInfo = useContext(ClientContext).sessionInfo?.userInfo;

  const expState = useExperiments();

  const treesModalExperiment = getExperimentVariant(
    expState.experiments,
    TREES_MODAL_EXPERIMENT
  );
  const isTreesModalExperiment = useMemo(
    () => treesModalExperiment === AVAILABLE,
    [treesModalExperiment]
  );

  const travelWalletOffer = getExperimentVariant(
    expState.experiments,
    TRAVEL_WALLET_OFFER_EXPERIMENT
  );
  const isTravelWalletOfferExperiment = React.useMemo(
    () => travelWalletOffer === AVAILABLE,
    [travelWalletOffer]
  );

  const travelWalletCreditsExperiment = getExperimentVariant(
    expState.experiments,
    TRAVEL_WALLET_CREDITS_EXPERIMENT
  );
  const isTravelWalletCreditsExperiment = React.useMemo(
    () => travelWalletCreditsExperiment === AVAILABLE,
    [travelWalletCreditsExperiment]
  );

  const creditAndOfferStackingExperimentV1 = getExperimentVariant(
    expState.experiments,
    CREDIT_OFFER_STACKING_V1
  );
  const isCreditAndOfferStackingExperimentV1 = useMemo(() => {
    return creditAndOfferStackingExperimentV1 === AVAILABLE;
  }, [creditAndOfferStackingExperimentV1]);

  const travelCreditHistoryExperiment = getExperimentVariant(
    expState.experiments,
    TRAVEL_CREDIT_HISTORY_EXPERIMENT
  );
  const isTravelCreditHistoryExperiment = useMemo(() => {
    return travelCreditHistoryExperiment === AVAILABLE;
  }, [travelCreditHistoryExperiment]);

  const DesktopVacationRentalsBookWorkflow = isCorpTenant(config.TENANT)
    ? CorpDesktopVacationRentalsBookWorkflow
    : ConsumerDesktopVacationRentalsBookWorkflow;

  const MobileVacationRentalsBookWorkflow = isCorpTenant(config.TENANT)
    ? CorpMobileVacationRentalsBookWorkflow
    : ConsumerMobileVacationRentalsBookWorkflow;

  const {
    isListingReady,
    isListingLoading: isVacationRentalBookLoading,
  } = useVacationRentalBookListing({ history, location, selectedHome });

  useEffect(() => {
    document.title = VACATION_RENTAL_CHECKOUT_TITLE;
    return () => {
      document.title = PORTAL_TITLE;
    };
  }, []);

  // Fire once this URL's listing is loaded, not on mount: on entry the store can
  // be empty (replatformed shop) or still hold a previous checkout's listing.
  const hasTrackedReviewDetails = useRef(false);
  useEffect(() => {
    if (hasTrackedReviewDetails.current || !isListingReady) {
      return;
    }
    hasTrackedReviewDetails.current = true;
    trackEvent({
      eventName: REVIEW_DETAILS_VR_CHECKOUT,
      properties: {
        ...viewedVacationRentalDetailsProperties.properties,
      },
      encryptedProperties: [
        ...viewedVacationRentalDetailsProperties.encryptedProperties,
      ],
    });
  }, [isListingReady, viewedVacationRentalDetailsProperties]);

  // Preselect primary traveler if the logged in user's information matches a traveler.
  // Only the id is latched — the object itself is resolved from the live list below,
  // so editing that traveler doesn't leave a stale pre-edit copy behind.
  useEffect(() => {
    if (userPassengerCallState === CallState.Success) {
      const savedPrimaryTraveler = travelers.find(
        (t) =>
          t.givenName === userInfo?.firstName &&
          t.surname === userInfo?.lastName
      );
      setSearchedPrimaryTraveler(true);
      if (!searchedPrimaryTraveler) {
        setPrimaryTravelerId(savedPrimaryTraveler?.id);
      }
    }
  }, [travelers, userPassengerCallState, searchedPrimaryTraveler]);

  const primaryTraveler = travelers.find((t) => t.id === primaryTravelerId);

  // Remount the desktop workflow when the searched dates/guests change (the only
  // place they're editable). Changing them re-fetches the shop listing and resets
  // the booking store (redoSearch); remounting resets the workflow's local state and
  // re-runs its mount effects (customer details / userInfo, initial price quote) so
  // the page ends up consistent — everything a reload would do, minus the reload.
  // Only date/guest params are included, so mobile's bookStep changes never remount.
  const bookSearchParams = new URLSearchParams(location.search);
  const desktopWorkflowKey = [
    bookSearchParams.get("fromDate"),
    bookSearchParams.get("untilDate"),
    bookSearchParams.get("adultsCount"),
    bookSearchParams.get("childrenCount"),
    bookSearchParams.get("petsCount"),
  ].join("|");

  return (
    <Box
      className={clsx(
        "vacation-rental-book-root",
        {
          confirm:
            location.pathname === PATH_VACATION_RENTALS_BOOK_CONFIRMATION,
        },
        { mobile: matchesMobile }
      )}
    >
      <Route path={PATH_VACATION_RENTALS_BOOK} exact>
        <Box className="vacation-rental-book-container">
          {matchesDesktop && isListingReady && (
            <Suspense>
              <DesktopVacationRentalsBookWorkflow
                key={desktopWorkflowKey}
                isTreesModalExperiment={isTreesModalExperiment}
                isTravelWalletOfferExperiment={isTravelWalletOfferExperiment}
                isCreditAndOfferStackingExperimentV1={
                  isCreditAndOfferStackingExperimentV1
                }
                isTravelCreditHistoryExperiment={
                  isTravelCreditHistoryExperiment
                }
                primaryTraveler={primaryTraveler}
              />
            </Suspense>
          )}
          {matchesMobile && isListingReady && (
            <Suspense>
              <MobileVacationRentalsBookWorkflow
                isTreesModalExperiment={isTreesModalExperiment}
                isTravelWalletOfferExperiment={isTravelWalletOfferExperiment}
                isTravelWalletCreditsExperiment={
                  isTravelWalletCreditsExperiment
                }
                isCreditAndOfferStackingExperimentV1={
                  isCreditAndOfferStackingExperimentV1
                }
                isTravelCreditHistoryExperiment={
                  isTravelCreditHistoryExperiment
                }
                primaryTraveler={primaryTraveler}
              />
            </Suspense>
          )}
        </Box>
        <VRBookingErrorModal />
        <BookingInProgressModal />
        <LoadingPopup
          indicatorSize="small"
          indicator={B2BSpinner}
          open={isVacationRentalBookLoading}
          popupSize="small"
          message="Checking availability"
        />
      </Route>
      <Route path={[PATH_VACATION_RENTALS_BOOK_CONFIRMATION]}>
        <BookingSuccessModal isTreesModalExperiment={isTreesModalExperiment} />
      </Route>
    </Box>
  );
};
